import { useApi } from '@/api';
import { EntityName, TableSkeleton } from '@/components';
import {
  CryptoIcon,
  CurrencyValue,
  DataTable,
  Heading,
  PercentageValue,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

export default function AllocationTable({ daysAgo }) {
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '-supply',
    filtersDebounceTime: 50
  });
  const [data, error] = useApi({
    url: '/morpho/allocations/',
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Collateral',
        getCell: (row) =>
          row.collateral_token_symbol ? (
            <EntityName
              symbol1={<CryptoIcon name={row.collateral_token_symbol} />}>
              {row.collateral_token_symbol}
            </EntityName>
          ) : (
            'Idle'
          ),
        columnSize: ['minmax(100px, auto)']
      },
      {
        header: 'LTV',
        getCell: (row) => <PercentageValue value={row.lltv} options={{}} />,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Supply',
        getCell: (row) => <CurrencyValue value={row.supply} />,
        getCellBottom: (row) => (
          <CurrencyValue
            fontSize="-1"
            value={row.supply - row.prev_supply}
            options={{
              trend: true,
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Supply cap',
        getCell: (row) =>
          row.collateral_token_symbol ? <CurrencyValue value={row.cap} /> : '-',
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Supply cap utilization',
        getCell: (row) => <PercentageValue value={row.supply / row.cap} />,
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Supply APY',
        getCell: (row) => (
          <>
            <PercentageValue value={row.supply_rate_apy} />
          </>
        ),
        getCellBottom: (row) => (
          <PercentageValue
            fontSize="-1"
            value={row.supply_rate_apy - row.prev_supply_rate_apy}
            options={{
              trend: true,
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Share in market',
        getCell: (row) => (
          <PercentageValue value={row.supply / row.total_supply} />
        ),

        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Collateral',
        getCell: (row) => (
          <CurrencyValue
            value={(row.supply / row.total_supply) * row.total_collateral}
            options={{
              currency: row.collateral_token_symbol,
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'LTV exposure',
        getCell: (row) => (
          <PercentageValue
            value={
              row.supply /
              ((row.supply / row.total_supply) * row.total_collateral)
            }
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Market Supply',
        getCell: (row) => <CurrencyValue value={row.total_supply} />,
        getCellBottom: (row) => (
          <CurrencyValue
            fontSize="-1"
            value={row.total_supply - row.prev_total_supply}
            options={{
              trend: true,
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      },
      {
        header: 'Market Utilization',
        getCell: (row) => <PercentageValue value={row.utilization} />,
        getCellBottom: (row) => (
          <PercentageValue
            fontSize="-1"
            value={row.utilization - row.prev_utilization}
            options={{
              trend: true,
              iconSize: '-1'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end'
      }
    ];

    return (
      <>
        <Heading size="2">Allocations</Heading>
        <DataTable
          rows={data['results']}
          columns={columns}
          getKey={(row) => row.group}
          sorting={sorting}
          pagination={{
            ...pagination,
            totalRecords: data.count
          }}
        />
      </>
    );
  }
  return <TableSkeleton />;
}
